import Decimal from "decimal.js";
import { getProperty } from "dot-prop";
import { z } from "zod";
import { isDefined } from "~/components/helpers";
import {
  ClassificationCategory,
  ClassificationType,
  InvoiceType,
  isDeductedTax,
  isExpensesCategory,
  isExpensesType,
  isIncomeCategory,
  isIncomeType,
  isVATCategoryWithRate,
  measurementUnit,
  taxType,
  TaxType,
  VATCategoryWithRate,
  vatRates,
} from "~/mydata";
import { itemTax } from "~/schemas/invoice-item";
import {
  expensesTotal,
  incomeTotal,
  InvoiceItemSchema,
  invoiceTotals,
} from "~/schemas/invoices";
import { findTax } from "./taxes";

export type InvoiceItem = z.input<InvoiceItemSchema>;
export type ItemTax = z.input<typeof itemTax>;

export const itemDiscount = (item: InvoiceItem) =>
  item.discount === undefined
    ? undefined
    : new Decimal(item.discount ?? 0);

export const itemNetValueNoDiscount = (item: InvoiceItem) =>
  item.netValuePerUnit === undefined
    ? undefined
    : new Decimal(item.netValuePerUnit)
    .mul(new Decimal(item.quantity ?? 1));

export const itemNetValue = (item: InvoiceItem) =>
  item.netValuePerUnit === undefined
    ? undefined
    : new Decimal(item.netValuePerUnit)
    .mul(new Decimal(item.quantity ?? 1))
    .minus(itemDiscount(item) ?? 0);

type ItemCalculation = (item: InvoiceItem) => Decimal | undefined;
const sumAmounts = (total: Decimal, amount?: Decimal.Value) =>
  amount ? total.add(amount) : total;

const itemVATEntry = (item: InvoiceItem) => {
  const category = item.vat.vatCategory;
  if (isVATCategoryWithRate(category)) {
    const vatRate = new Decimal(vatRates[category]).mul("0.01");
    const amount = itemNetValue(item)?.mul(vatRate);
    return amount === undefined
      ? undefined
      : ([category, amount] as [typeof category, Decimal]);
  }
  return undefined;
};
export const itemVAT: ItemCalculation = (item: InvoiceItem) => {
  const [_, amount = undefined] = itemVATEntry(item) ?? [];
  return amount;
};
export const itemTotalVAT = (...items: InvoiceItem[]) =>
  items.map(itemVAT).reduce(sumAmounts, new Decimal(0));

export const itemGrossTotal = (...items: InvoiceItem[]) =>
  items.map(itemGrossValue).reduce(sumAmounts, new Decimal(0));

export const itemGrossTotalNoTaxes = (...items: InvoiceItem[]) =>
  items.map(itemGrossValueNoTaxes).reduce(sumAmounts, new Decimal(0));

export const itemTotalNetValue = (...items: InvoiceItem[]) =>
  items.map(itemNetValue).reduce(sumAmounts, new Decimal(0));

export const calculateRemainingAmount = (grossAmt: string, paidAmt: string): string => (parseFloat(grossAmt) - parseFloat(paidAmt)).toFixed(2);

export const itemVATSummary = (...items: InvoiceItem[]) =>
  Object.entries(
    items
      .map(itemVATEntry)
      .filter(isDefined)
      .reduce(
        (summary, [category, amount]) => ({
          ...summary,
          [category]: amount.add(summary[category] ?? 0),
        }),
        {} as Record<VATCategoryWithRate, Decimal>
      )
  ).map(
    ([cat, amount]) => [Number(cat), amount] as [VATCategoryWithRate, Decimal]
  );

export const itemTaxEntries = (...items: InvoiceItem[]) =>
  items.flatMap((item) =>
    (item.taxes ?? []).map((tax) => [tax, item] as [ItemTax, InvoiceItem])
  );

export const itemTaxAmount = (tax: ItemTax, item: InvoiceItem) => {
  const meta = findTax(tax.type, tax.category);
  if (meta === undefined) {
    return undefined;
  }
  const customAmount = getProperty(meta, "customAmount", false);
  if (customAmount) {
    return tax.customAmount === undefined
      ? undefined
      : new Decimal(tax.customAmount);
  }
  const rate = getProperty(meta, "rate", NaN);
  if (!Number.isNaN(rate)) {
    const netValue = itemNetValue(item);
    return netValue?.mul(new Decimal(rate).mul("0.01"));
  }
  const amountPerItem = getProperty(meta, "amountPerItem", NaN);
  if (!Number.isNaN(amountPerItem)) {
    const { quantity, measurementUnit: unit = measurementUnit.Items } = item;
    return unit === measurementUnit.Items
      ? new Decimal(amountPerItem).mul(new Decimal(quantity ?? 1))
      : undefined;
  }
};

export const itemTotalDeductedTaxAmount = (...items: InvoiceItem[]) =>
  itemTaxEntries(...items)
    .filter(([tax, _]) => isDeductedTax(tax.type))
    .map(([tax, item]) => itemTaxAmount(tax, item))
    .reduce(sumAmounts, new Decimal(0));

export const itemTotalTaxAmount = (...items: InvoiceItem[]) =>
  itemTaxEntries(...items)
    .filter(([tax, _]) => !isDeductedTax(tax.type))
    .map(([tax, item]) => itemTaxAmount(tax, item))
    .reduce(sumAmounts, new Decimal(0));

export const itemTaxSummary = (...items: InvoiceItem[]) =>
  Object.entries(
    items
      .flatMap((item) => itemTaxEntries(item))
      .reduce(
        (summary, [tax, item]) => ({
          ...summary,
          [tax.type]: (summary[tax.type] ?? new Decimal(0)).add(
            itemTaxAmount(tax, item) ?? 0
          ),
        }),
        {} as Record<TaxType, Decimal | undefined>
      )
  ).map(([type, amount]) => [Number(type), amount] as [TaxType, Decimal]);

const itemClassificationTotals = (...items: InvoiceItem[]) =>
  items
    .map(({ classification: { type, category } = {}, ...item }) =>
      type
        ? {
            category,
            type,
            amount: itemNetValue(item as any) ?? new Decimal(0),
          }
        : undefined
    )
    .filter(isDefined)
    .reduce((entries, { category, type, amount }) => {
      const index = entries.findIndex(
        (e) => e.category === category && e.type === type
      );
      return index === -1
        ? [...entries, { type, category, amount }]
        : [
            ...entries.slice(0, index),
            { type, category, amount: amount.add(entries[index].amount) },
            ...entries.slice(index + 1),
          ];
    }, [] as { category?: ClassificationCategory; type: ClassificationType; amount: Decimal }[]);

const itemVatClassificationTotals = (...items: InvoiceItem[]) =>
	items
		.map(({ vatClassification: { type } = {}, ...item }) =>
			type
				? {
					type,
					amount: itemNetValue(item as any) ?? new Decimal(0)
				}
				:undefined
		)
		.filter(isDefined)
		.reduce((entries, { type, amount }) => {
			const index = entries.findIndex(
				(e) => e.type === type
			);

			return index === -1
				? [...entries, { type, amount }]
				:[
					...entries.slice(0, index),
					{ type, amount: amount.add(entries[index].amount) },
					...entries.slice(index + 1)
				];
		}, [] as {type: ClassificationType, amount: Decimal}[]);

export const itemIncomeClassificationsSummary = (...items: InvoiceItem[]) =>
  itemClassificationTotals(...items)
    .filter(
      (entry) => isIncomeType(entry.type) && isIncomeCategory(entry.category)
    )
    .map(
      (entry) =>
        ({
          ...entry,
          amount: entry.amount.toFixed(2),
        } as z.output<typeof incomeTotal>)
    );

export const itemExpensesVatClassificationsSummary = (...items: InvoiceItem[]) =>
	itemVatClassificationTotals(...items)
	  .filter(
			(entry) =>
				isExpensesType(entry.type)
		)
		.map(
			(entry) => ({
				...entry,
        amount: entry.amount.toFixed(2)
			})
		);

export const itemExpensesClassificationsSummary = (...items: InvoiceItem[]) =>
  itemClassificationTotals(...items)
    .filter(
      (entry) =>
        isExpensesType(entry.type) && isExpensesCategory(entry.category)
    )
    .map(
      (entry) =>
        ({
          ...entry,
          amount: entry.amount.toFixed(2),
        } as z.output<typeof expensesTotal>)
    );

export const invoiceSummary = (
  invoiceType: InvoiceType,
  items: InvoiceItem[]
): z.input<typeof invoiceTotals> => {
  const taxes = Object.fromEntries(itemTaxSummary(...items));
  return {
    totalNetValue: items
      .map(itemNetValue)
      .reduce(sumAmounts, new Decimal(0))
      .toFixed(2),
    totalVAT: items.map(itemVAT).reduce(sumAmounts, new Decimal(0)).toFixed(2),
    totalWithheldTax: taxes[taxType.Withheld]?.toFixed(2) ?? "0",
    totalStampDuty: totalTax(taxType.StampDuty, ...items).toFixed(2),
    totalFee: totalTax(taxType.Fee, ...items).toFixed(2),
    totalOtherTax: totalTax(taxType.OtherTax, ...items).toFixed(2),
    totalDeductions: totalTax(taxType.Deductions, ...items).toFixed(2),
    totalExpenses: itemExpensesClassificationsSummary(...items),
    totalIncome: itemIncomeClassificationsSummary(...items),
    totalGrossValue: itemGrossTotal(...items).toFixed(2),
  };
};
export const totalTax = (type: TaxType, ...items: InvoiceItem[]) =>
  itemTaxEntries(...items)
    .filter(([tax, _]) => tax.type === type)
    .map(([tax, item]) => itemTaxAmount(tax, item))
    .reduce(sumAmounts, new Decimal(0));

export const itemGrossValue = (item: InvoiceItem) =>
  itemNetValue(item)
  ?.add(itemVAT(item) ?? 0)
  .add(itemTotalTaxAmount(item))
  .minus(itemTotalDeductedTaxAmount(item))

export const itemGrossValueNoTaxes = (item: InvoiceItem) =>
  itemNetValue(item)
  ?.add(itemVAT(item) ?? 0);